import React, { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * PWA Install Banner - Compact banner for admin pages
 * Shows at the top of the page on mobile devices
 */
const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already in standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone;

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa_banner_dismissed');
    if (dismissed) {
      const dismissedAt = new Date(dismissed);
      const hoursSinceDismissed = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        return; // Don't show for 24 hours after dismiss
      }
    }

    // Detect device
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isMobile = isIOSDevice || /android/.test(userAgent);
    setIsIOS(isIOSDevice);

    if (isMobile && !isStandalone) {
      // Listen for beforeinstallprompt (Android)
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowBanner(true);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstall);

      // For iOS, show after delay
      if (isIOSDevice) {
        setTimeout(() => setShowBanner(true), 1500);
      }

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      };
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa_banner_dismissed', new Date().toISOString());
  };

  if (!showBanner) return null;

  return (
    <div 
      className="bg-gradient-to-r from-tp-primary to-tp-accent text-white px-4 py-3 flex items-center justify-between gap-3"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Download className="h-5 w-5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">
            {isIOS ? 'Installez l\'app' : 'Installer l\'application'}
          </p>
          {isIOS && (
            <p className="text-xs text-white/80 truncate">
              Partager → Sur l'écran d'accueil
            </p>
          )}
        </div>
      </div>
      
      {!isIOS && deferredPrompt && (
        <Button
          size="sm"
          variant="secondary"
          onClick={handleInstall}
          className="bg-white text-tp-primary hover:bg-white/90 flex-shrink-0"
        >
          Installer
        </Button>
      )}
      
      <button
        onClick={handleDismiss}
        className="p-1 hover:bg-white/20 rounded flex-shrink-0"
        aria-label="Fermer"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default PWAInstallBanner;
